// "Recent runs" in Agents' overview (agents.runs, gap #18): the latest runs of every agent, or one,
// newest first: what each was asked, how it ended and its steps, a flag in words, and the cost for
// the owner only (agents.manage). Open run shows its steps (agents.getRun).

import { Fragment, useState } from 'react';
import { Empty, Panel, useQuery } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { initials, taskText } from './model.ts';
import { RunPanel } from './RunPanel.tsx';
import type { AgentsProps } from './props.ts';
import {
  costText,
  flagWords,
  runLine,
  runStatus,
  startedText,
  triggerWords,
  type RunSummary,
} from './runs-model.ts';

export function RecentRuns({ props, names }: { props: AgentsProps; names: Map<string, string> }) {
  const [agentKey, setAgentKey] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const runs = useQuery<{ runs: RunSummary[] }>(
    'agents.runs',
    agentKey ? { agentKey, limit: 25 } : { limit: 25 },
  );
  return (
    <Panel
      title="Recent runs"
      aside={
        <div className="row-actions">
          <label className="sr" htmlFor="runs-agent">
            Agent
          </label>
          <select
            id="runs-agent"
            className="field"
            value={agentKey}
            onChange={(e) => setAgentKey(e.target.value)}
          >
            <option value="">Every agent</option>
            {[...names].map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {!runs.data ? (
        <Loading error={runs.error} what="the recent runs" kind="table" retry={() => void runs.reload()} />
      ) : runs.data.runs.length === 0 ? (
        <Empty>No runs yet.</Empty>
      ) : (
        <ul className="rows log" aria-label="Recent runs">
          {runs.data.runs.map((r) => {
            const [label, tone] = runStatus(r.status);
            return (
              <Fragment key={r.id}>
                <li className="log-row run-row">
                  <span className="time small">
                    {startedText(
                      r.startedAt,
                      props.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                    )}
                  </span>
                  <span className="agav" aria-hidden="true">
                    {initials(names.get(r.agentKey) ?? r.agentKey)}
                  </span>
                  <div className="log-main">
                    <div>
                      <b>{names.get(r.agentKey) ?? r.agentKey}</b>{' '}
                      <span className="muted small">· {triggerWords(r.trigger)}</span>
                    </div>
                    <span className="small">{taskText(r.task)}</span>
                    <span className="muted small block">
                      {runLine(r)}
                      {props.isOwner && r.costCents !== undefined
                        ? ` · model cost ${costText(r.costCents)}`
                        : ''}
                    </span>
                    {flagWords(r.flags).map((f) => (
                      <p key={f} className="chip bad">
                        {f}
                      </p>
                    ))}
                  </div>
                  <span className={`chip say ${tone}`}>{label}</span>
                  <button
                    type="button"
                    className="btn quiet"
                    aria-expanded={open === r.id}
                    onClick={() => setOpen(open === r.id ? null : r.id)}
                  >
                    {open === r.id ? 'Hide run' : 'Open run'}
                  </button>
                </li>
                {open === r.id && (
                  <li className="log-detail">
                    <RunPanel runId={r.id} timeZone={props.timeZone} showCost={props.isOwner} />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
